import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import * as api from '../api';

import { ModalitiesConfigurationRoutes } from '../scenes/AssistantRenders/routes';
import { isEnumMember } from 'common/dist/utils/types';
import { loadedInIframe } from '../utils/message';
import { QueryKey } from '../common/queryKey';

export const useConfiguration = () => {
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [reconciliationStatus, setReconciliationStatus] = useState<
    api.Previsit.ReconciliationStatus | undefined
  >();
  const [shownInViewport, setShownInViewport] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const configurationId = searchParams.get('configurationId');
  const pathname = location.pathname;

  const {
    data: configuration,
    status,
    refetch: refetchConfiguration,
  } = useQuery({
    queryKey: [QueryKey.GetConfiguration, configurationId, pathname],
    queryFn: ({ queryKey }) => {
      const [, configurationId, pathname] = queryKey;
      if (!configurationId) return;
      const modality = Object.keys(ModalitiesConfigurationRoutes).find(
        (key) => {
          if (
            pathname ===
            ModalitiesConfigurationRoutes[
              key as keyof typeof ModalitiesConfigurationRoutes
            ].path
          ) {
            return true;
          }
          return false;
        },
      );
      return api.client.Assistant.getConfigurationById(
        configurationId,
        isEnumMember(api.Assistant.Modality, modality) ? modality : undefined,
        loadedInIframe(),
      );
    },
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { data: isAuthenticated } = useQuery({
    queryKey: [QueryKey.IsAuthenticated, configurationId],
    queryFn: async ({ queryKey }) => {
      const [, configurationId] = queryKey;
      if (!configurationId) return;
      const { isAuthenticated } =
        await api.client.Previsit.getIsPatientAuthenticated(configurationId);
      return isAuthenticated;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  const submitAssistantVisible = useMutation({
    mutationKey: [QueryKey.OnVisibleInViewport],
    mutationFn: async () => {
      if (!configurationId || !configuration?.sessionId) {
        return;
      }
      return api.client.Assistant.createLog({
        action: api.Log.AssistantAuditAction.AssistantVisible,
        assistantId: configurationId,
        sessionId: configuration.sessionId,
      });
    },
  });

  useEffect(() => {
    setAuthenticated(!!isAuthenticated);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!configuration) return;
    const {
      fontFamily,
      fontSize,
      paragraphFontFamily,
      inputBorderRadiusPx,
      actionColor,
      borderColor,
      backgroundColor,
      fontColor,
      heroCommonQuestionBackgroundColor,
      heroCommonQuestionIconColor,
      heroCommonQuestionTextColor,
      heroCommonQuestionsHeaderColor,
      heroGradientOneColor,
      heroGradientTwoColor,
      heroGreetingMessageColor,
      heroGreetingMessageFontFamily,
      heroGreetingMessageFontSizeDesktop,
      heroGreetingMessageFontSizeMobile,
      heroGreetingMessageFontWeight,
      heroInputFontFamily,
      heroInputFontSizeDesktop,
      heroInputFontSizeMobile,
      conversationFontFamily,
      conversationFontSizeDesktop,
      conversationFontSizeMobile,
      buttonBorderRadiusPx,
      inputHelperTextColor,
      eyebrowFontFamily,
      eyebrowFontSizeDesktop,
      eyebrowFontSizeMobile,
    } = configuration;
    [
      { value: fontFamily, property: '--font-family' },
      { value: fontSize, property: '--font-size' },
      { value: paragraphFontFamily, property: '--paragraph-font-family' },
      { value: inputBorderRadiusPx, property: '--input-border-radius' },
      { value: actionColor, property: '--action-color' },
      { value: borderColor, property: '--border-color' },
      { value: backgroundColor, property: '--background-color' },
      { value: fontColor, property: '--font-color' },
      { value: heroGreetingMessageColor, property: '--greeting-color' },
      {
        value: heroGreetingMessageFontFamily,
        property: '--greeting-font-family',
      },
      {
        value: heroGreetingMessageFontSizeDesktop,
        property: '--greeting-font-size-desktop',
      },
      {
        value: heroGreetingMessageFontSizeMobile,
        property: '--greeting-font-size-mobile',
      },
      { value: heroInputFontFamily, property: '--hero-input-font-family' },
      {
        value: heroInputFontSizeDesktop,
        property: '--hero-input-font-size-desktop',
      },
      {
        value: heroInputFontSizeMobile,
        property: '--hero-input-font-size-mobile',
      },
      { value: conversationFontFamily, property: '--conversation-font-family' },
      {
        value: conversationFontSizeDesktop,
        property: '--conversation-font-size-desktop',
      },
      {
        value: conversationFontSizeMobile,
        property: '--conversation-font-size-mobile',
      },
      { value: buttonBorderRadiusPx, property: '--button-border-radius' },
      {
        value: heroCommonQuestionBackgroundColor,
        property: '--common-question-background-color',
      },
      {
        value: heroGreetingMessageFontWeight,
        property: '--greeting-font-weight',
      },
      {
        value: heroCommonQuestionIconColor,
        property: '--common-question-icon-color',
      },
      {
        value: heroCommonQuestionTextColor,
        property: '--common-question-text-color',
      },
      {
        value: heroGradientOneColor,
        property: '--gradient-one-color',
      },
      {
        value: heroGradientTwoColor,
        property: '--gradient-two-color',
      },
      {
        value: heroCommonQuestionsHeaderColor,
        property: '--common-questions-header-color',
      },
      {
        value: inputHelperTextColor,
        property: '--input-helper-text-color',
      },
      {
        value: eyebrowFontFamily,
        property: '--eyebrow-font-family',
      },
      {
        value: eyebrowFontSizeDesktop,
        property: '--eyebrow-font-size-desktop',
      },
      {
        value: eyebrowFontSizeMobile,
        property: '--eyebrow-font-size-mobile',
      },
    ].forEach(({ value, property }) => {
      if (!value) return;
      document.documentElement.style.setProperty(property, value);
    });
  }, [configuration]);

  return {
    ...configuration,
    authenticated,
    setAuthenticated,
    reconciliationStatus,
    setReconciliationStatus,
    isLoading: status === 'pending',
    onVisibleInViewport: async () => {
      if (shownInViewport) return;
      setShownInViewport(true);
      await submitAssistantVisible.mutateAsync();
    },
    reset: refetchConfiguration,
  };
};
